import { getRuntimeConfig } from '~/shared/utils/config';
import Auth from './authentication';
import { AltaPay } from './types';
import { AltaPayRequestInit, DefaultPayload, request } from './lib/request';
import { isSessionNotFound, isUnauthorized } from './lib/errors';
import { createAbsoluteUrl } from './lib/utils';

type Session = AltaPay.Schemas['Session'];

interface InitializationConfig {
    existingSessionId?: string;
}

const getBrowserContext = (): AltaPay.Schemas['Context'] | undefined => {
    if (typeof window === 'undefined') return undefined;

    return {
        browser: {
            agent: window.navigator.userAgent,
            origin: window.location.origin,
            language: window.navigator.language,
            resolution: `${window.screen.width}x${window.screen.height}`,
        },
    };
};

export const generateAltaPayApiUrl = (relativeUrl: string) => {
    const { config } = getRuntimeConfig();

    const proxyUrl = config.NEXT_PUBLIC_URL.includes('localhost')
        ? config.NEXT_PUBLIC_ALTAPAY_PROXY_URL
        : undefined;

    /**
     * We'll proxy our requests to the specified environment when we are on our localhost.
     * AltaPay requires acccessible endpoints for these callbacks.
     */
    const baseUrl = proxyUrl || config.NEXT_PUBLIC_URL;

    return createAbsoluteUrl(relativeUrl, baseUrl || config.NEXT_PUBLIC_URL);
};

type AuthorizedRequest = InstanceType<typeof AltaPaySession>['authorizedRequest'];

export class AltaPaySession {
    private _sessionPromise: Promise<Session> | undefined;
    private _sessionCreatedAt: number | undefined;

    // Server-side sessions only use the merchant token they are given. They never get a new one.
    private readonly _serverToken: string | undefined;

    token: string | undefined;
    private _sessionId: string | undefined;

    constructor(serverToken?: string) {
        this._sessionId = undefined;
        this.token = serverToken;
        this._serverToken = serverToken;

        this._sessionPromise = undefined;
        this._sessionCreatedAt = undefined;

        this._establishSession = this._establishSession.bind(this);
    }

    private get _isServerSession() {
        return this._serverToken !== undefined;
    }

    getSessionId() {
        return this._sessionId;
    }

    getSessionAgeInSeconds() {
        if (!this._sessionCreatedAt) return undefined;

        return Math.round((Date.now() - this._sessionCreatedAt) / 1000);
    }

    clearSession() {
        this._sessionId = undefined;
        this.token = this._serverToken;
        this._sessionPromise = undefined;
        this._sessionCreatedAt = undefined;
    }

    private async _getToken(): Promise<string> {
        if (this._serverToken) return this._serverToken;

        // A session keeps its token until AltaPay rejects it.
        if (this._sessionId && this.token) return this.token;

        this.token = await Auth.getValidToken();

        return this.token;
    }

    authorizedRequest = async <
        ResponseType = unknown,
        PayloadType extends DefaultPayload = DefaultPayload,
    >(
        path: string,
        options?: AltaPayRequestInit<PayloadType>,
    ): Promise<ResponseType> => {
        const send = (token: string) =>
            request<ResponseType, PayloadType>(path, {
                ...options,
                headers: { Authorization: `Bearer ${token}` },
            });

        try {
            const token = await this._getToken();

            return await send(token);
        } catch (error) {
            // We will re-attempt the request with fresh auth if we are not in a server session.
            if (!isUnauthorized(error) || this._isServerSession) throw error;

            this.token = await Auth.reauthenticate(this._sessionId);

            return send(this.token);
        }
    };

    private async _establishSession(config?: InitializationConfig): Promise<Session> {
        const id = config?.existingSessionId || this._sessionId;

        const options: Parameters<AuthorizedRequest> = id
            ? [`/session/${id}`]
            : [
                  '/session',
                  {
                      method: 'POST',
                      body: {
                          callbacks: {
                              bodyFormat: 'JSON',
                              /**
                               * 'verifyOrder' is called before a payment transaction gets completed.
                               * On this webook, we will attempt to create the order. If it's successful,
                               * we will proceed with the payment. Otherwise, it will get cancelled.
                               */
                              verifyOrder: generateAltaPayApiUrl('/api/altapay/verify').toString(),
                              /**
                               * 'notification' is called everytime the status of a payment transaction updates.
                               * We use this as a failsafe for pending or payments to ensure that they have equivalent orders.
                               */
                              notification: generateAltaPayApiUrl(
                                  '/api/altapay/notifications',
                              ).toString(),
                          },
                          context: getBrowserContext(),
                          configuration: {
                              country: 'DK',
                              language: 'da',
                          },
                      },
                  },
              ];

        try {
            const session = await this.authorizedRequest<Session, Session>(...options);

            // We will save a new timestamp, if no existing session ID was passed, to mark creation.
            if (!id) this._sessionCreatedAt = Date.now();

            return session;
        } catch (error) {
            // Existing sessions can expire on AltaPay's side. Server sessions must keep the given session.
            if (id && !this._isServerSession && isSessionNotFound(error)) {
                this.clearSession();

                return this._establishSession();
            }

            throw error;
        }
    }

    async initialize(config?: InitializationConfig) {
        if (this._sessionPromise) return this._sessionPromise;

        const startSession = async () => {
            const session = await this._establishSession(config);
            this._sessionId = session.sessionId;

            return session;
        };

        this._sessionPromise = startSession().finally(() => {
            this._sessionPromise = undefined;
        });

        return this._sessionPromise;
    }
}

/**
 * We will be using a singleton for our client-side AltaPay sessions
 * to ensure that we will always have ONLY ONE active instance on the browser.
 */
export const ClientSideSingleton = new AltaPaySession();

export default AltaPaySession;
